// Wykrywanie bezczynności użytkownika — wspólne dla całego panelu.
//
// Dwa progi, uzgodnione z klientem:
//   * PAUZA (10 min)     — użytkownik przestaje być uznawany za pracującego.
//                          Dziś tylko sygnalizowane przez `onIdle`; docelowo
//                          zatrzyma naliczanie czasu w ewidencji (heartbeat).
//   * WYLOGOWANIE (45 min) — sesja się kończy. Minutę wcześniej panel pyta,
//                          czy ktoś jeszcze jest.
//
// Znacznik ostatniej aktywności trzymamy w localStorage, więc ruch w JEDNEJ
// karcie liczy się dla wszystkich otwartych kart tego samego panelu.
//
// W trakcie ostrzeżenia zdarzenia bierne (ruch myszy) są ignorowane —
// sesję przedłuża wyłącznie świadome kliknięcie. Inaczej drgnięcie myszy
// albo kot na klawiaturze trzymałyby sesję otwartą w nieskończoność.

const ACTIVITY_KEY = 'lastActivityAt';
const RECORD_THROTTLE_MS = 1000;
const CHECK_INTERVAL_MS = 5000;

export const IDLE_PAUSE_MIN = 10;
export const AUTO_LOGOUT_MIN = 45;
export const WARN_BEFORE_SEC = 60;

// Wartości domyślne wyżej są punktem wyjścia; ewidencja czasu pracy trzyma
// własne progi w konfiguracji firmy i podaje je przy starcie obserwacji.
let pauseMin = IDLE_PAUSE_MIN;
let logoutMin = AUTO_LOGOUT_MIN;
let warnSec = WARN_BEFORE_SEC;

const PASSIVE_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel', 'click'];

let timer = null;
let lastRecorded = 0;
let phase = 'active'; // active | idle | warning
let cb = {};
let listening = false;

function now() { return Date.now(); }

function readLastActivity() {
  try {
    const v = parseInt(localStorage.getItem(ACTIVITY_KEY), 10);
    return Number.isFinite(v) ? v : now();
  } catch (e) {
    return now();
  }
}

function writeLastActivity(ts) {
  try { localStorage.setItem(ACTIVITY_KEY, String(ts)); } catch (e) { /* prywatny tryb */ }
}

/**
 * Odnotowuje aktywność. `force` pomija throttle i blokadę w fazie ostrzeżenia —
 * używane przez przycisk „Jestem tutaj".
 */
export function markActivity(force = false) {
  if (!force) {
    if (phase === 'warning') return;
    if (now() - lastRecorded < RECORD_THROTTLE_MS) return;
  }
  lastRecorded = now();
  writeLastActivity(lastRecorded);
  // Przy świadomym kliknięciu reagujemy natychmiast, żeby okno ostrzeżenia
  // zniknęło od razu. Poza tym o fazie decyduje `tick()` na podstawie czasu.
  if (force) setPhase('active');
}

/** Ile milisekund minęło od ostatniej aktywności (w dowolnej karcie). */
export function idleMs() { return now() - readLastActivity(); }

function onPassiveEvent() { markActivity(false); }

/**
 * Ustawia fazę i zgłasza ją tylko przy realnej zmianie.
 * Faza wynika z upływu czasu — gdyby zależała od tego, czy zdarzenie
 * przeszło przez throttle, potrafiłaby utknąć na „pauzie" na stałe.
 */
function setPhase(next) {
  if (phase === next) return false;
  phase = next;
  if (next === 'active' && cb.onActive) cb.onActive();
  if (next === 'idle' && cb.onIdle) cb.onIdle();
  return true;
}

function tick() {
  const idle = idleMs();
  const logoutMs = logoutMin * 60000;
  const warnMs = logoutMs - warnSec * 1000;
  const pauseMs = pauseMin * 60000;

  if (idle >= logoutMs) {
    stopIdleWatch();
    if (cb.onTimeout) cb.onTimeout();
    return;
  }

  if (idle >= warnMs) {
    const secondsLeft = Math.max(0, Math.ceil((logoutMs - idle) / 1000));
    if (phase !== 'warning') {
      phase = 'warning';
      if (cb.onWarn) cb.onWarn(secondsLeft);
    } else if (cb.onWarnTick) {
      cb.onWarnTick(secondsLeft);
    }
    return;
  }

  setPhase(idle >= pauseMs ? 'idle' : 'active');
}

/**
 * Uruchamia obserwację.
 * @param {{onIdle?:Function, onActive?:Function, onWarn?:Function,
 *          onWarnTick?:Function, onTimeout?:Function}} handlers
 * @param {{idlePauseMin?:number, autoLogoutMin?:number, warnBeforeSec?:number}} thresholds
 *        progi z konfiguracji ewidencji; pominięte = wartości domyślne
 */
export function startIdleWatch(handlers = {}, thresholds = {}) {
  stopIdleWatch();
  cb = handlers;
  pauseMin = thresholds.idlePauseMin || IDLE_PAUSE_MIN;
  logoutMin = thresholds.autoLogoutMin || AUTO_LOGOUT_MIN;
  warnSec = thresholds.warnBeforeSec || WARN_BEFORE_SEC;
  phase = 'active';
  markActivity(true);

  if (!listening) {
    PASSIVE_EVENTS.forEach((ev) => window.addEventListener(ev, onPassiveEvent, { passive: true }));
    listening = true;
  }
  timer = setInterval(tick, CHECK_INTERVAL_MS);
}

export function stopIdleWatch() {
  if (timer) { clearInterval(timer); timer = null; }
  if (listening) {
    PASSIVE_EVENTS.forEach((ev) => window.removeEventListener(ev, onPassiveEvent));
    listening = false;
  }
  phase = 'active';
}

export default { startIdleWatch, stopIdleWatch, markActivity, idleMs, IDLE_PAUSE_MIN, AUTO_LOGOUT_MIN, WARN_BEFORE_SEC };
